<!-- Create / Edit User -->
<div class="create-user-container">
  <!-- Breadcrumb -->
  <nav class="breadcrumb-nav" aria-label="breadcrumb">
    <ol class="breadcrumb">
      <li class="breadcrumb-item">
        <a [routerLink]="['/admin/user']" class="breadcrumb-link">
          <i class="fa-solid fa-users me-1"></i>
          Users
        </a>
      </li>
      <li class="breadcrumb-item active" aria-current="page">
        @if(user_id == null) {
          Create New User
        } @else {
          Edit User
        }
      </li>
    </ol>
  </nav>

  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          @if(user_id == null) {
            <i class="fa-solid fa-user-plus"></i>
          } @else {
            <i class="fa-solid fa-user-pen"></i>
          }
        </div>
        <div class="header-text">
          <h1 class="page-title">
            @if(user_id == null) {
              Create New User
            } @else {
              Edit User Account
            }
          </h1>
          <p class="page-description">
            @if(user_id == null) {
              Add a new user to the system with role-based permissions
            } @else {
              Update user information and account settings
            }
          </p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary btn-sm"
                [disabled]="isSubmitting"
                (click)="router.navigateByUrl('/admin/user')">
          <i class="fa-solid fa-arrow-left"></i>
          Back to Users
        </button>
      </div>
    </div>
  </div>

  <!-- Alert Messages -->
  @if (successMessage) {
    <div class="alert-container">
      <ngb-alert #selfClosingAlert [type]="type" (closed)="successMessage = ''">
        <div class="alert-content">
          @if (type === 'info') {
            <i class="fa-solid fa-circle-info me-2"></i>
          } @else if (type === 'success') {
            <i class="fa-solid fa-circle-check me-2"></i>
          } @else if (type === 'danger') {
            <i class="fa-solid fa-triangle-exclamation me-2"></i>
          }
          {{ message }}
        </div>
      </ngb-alert>
    </div>
  }

  <!-- Main Content -->
  <div class="content-grid">
    <div class="row g-4">
      <!-- User Form Section -->
      <div class="col-lg-8">
        <div class="form-card">
          <div class="card-section-header">
            <div class="d-flex align-items-center gap-3">
              <div class="section-icon">
                <i class="fa-solid fa-user-circle"></i>
              </div>
              <div>
                <h3 class="section-title">User Information</h3>
                <p class="section-subtitle">Enter the user's personal and account details</p>
              </div>
            </div>
          </div>

          <div class="card-section-body">
            @if (isLoadingUser) {
              <div class="loading-state">
                <div class="loading-content">
                  <div class="loading-spinner">
                    <div class="spinner-ring"></div>
                  </div>
                  <div class="loading-text">
                    <h5>Loading User Data</h5>
                    <p>Please wait while we fetch the user information...</p>
                  </div>
                </div>
              </div>
            } @else {
              <form [formGroup]="createForm" (ngSubmit)="OnSubmit()" class="user-form">
                <div class="form-grid">
                  <!-- First Name -->
                  <div class="form-group">
                    <label for="first_name" class="form-label">
                      First Name <span class="text-danger">*</span>
                    </label>
                    <input type="text"
                           id="first_name"
                           formControlName="first_name"
                           class="form-control"
                           [class.is-invalid]="isFieldInvalid('first_name')"
                           placeholder="Enter first name"
                           [disabled]="isSubmitting">
                    @if (isFieldInvalid('first_name')) {
                      <div class="invalid-feedback">
                        {{ getFieldError('first_name') }}
                      </div>
                    }
                  </div>

                  <!-- Last Name -->
                  <div class="form-group">
                    <label for="last_name" class="form-label">
                      Last Name <span class="text-danger">*</span>
                    </label>
                    <input type="text"
                           id="last_name"
                           formControlName="last_name"
                           class="form-control"
                           [class.is-invalid]="isFieldInvalid('last_name')"
                           placeholder="Enter last name"
                           [disabled]="isSubmitting">
                    @if (isFieldInvalid('last_name')) {
                      <div class="invalid-feedback">
                        {{ getFieldError('last_name') }}
                      </div>
                    }
                  </div>

                  <!-- Email -->
                  <div class="form-group">
                    <label for="email" class="form-label">
                      Email Address <span class="text-danger">*</span>
                    </label>
                    <input type="email"
                           id="email"
                           formControlName="email"
                           class="form-control"
                           [class.is-invalid]="isFieldInvalid('email')"
                           placeholder="Enter email address"
                           [disabled]="isSubmitting">
                    @if (isFieldInvalid('email')) {
                      <div class="invalid-feedback">
                        {{ getFieldError('email') }}
                      </div>
                    }
                  </div>

                  <!-- Phone -->
                  <div class="form-group">
                    <label for="phone" class="form-label">
                      Phone Number <span class="text-danger">*</span>
                    </label>
                    <input type="tel"
                           id="phone"
                           formControlName="phone"
                           class="form-control"
                           [class.is-invalid]="isFieldInvalid('phone')"
                           placeholder="Enter phone number"
                           [disabled]="isSubmitting">
                    @if (isFieldInvalid('phone')) {
                      <div class="invalid-feedback">
                        {{ getFieldError('phone') }}
                      </div>
                    }
                  </div>

                  <!-- Role -->
                  <div class="form-group">
                    <label for="role" class="form-label">
                      User Role <span class="text-danger">*</span>
                    </label>
                    @if (isLoadingRoles) {
                      <div class="loading-select">
                        <div class="select-spinner">
                          <i class="fa-solid fa-spinner fa-spin me-2"></i>
                          Loading roles...
                        </div>
                      </div>
                    } @else {
                      <select id="role"
                              formControlName="role"
                              class="form-select"
                              [class.is-invalid]="isFieldInvalid('role')"
                              [disabled]="isSubmitting">
                        <option value="">-- Select Role --</option>
                        @for(role of roles; track role.id) {
                          <option [value]="role.name">{{ capitalizeFirstLetter(role.name) }}</option>
                        }
                      </select>
                      @if (isFieldInvalid('role')) {
                        <div class="invalid-feedback">
                          {{ getFieldError('role') }}
                        </div>
                      }
                    }
                  </div>

                  <!-- Password -->
                  <div class="form-group">
                    <label for="password" class="form-label">
                      Password <span class="text-danger">*</span>
                    </label>
                    <input type="password"
                           id="password"
                           formControlName="password"
                           class="form-control"
                           [class.is-invalid]="isFieldInvalid('password')"
                           placeholder="Enter password (min. 6 characters)"
                           [disabled]="isSubmitting">
                    @if (isFieldInvalid('password')) {
                      <div class="invalid-feedback">
                        {{ getFieldError('password') }}
                      </div>
                    }
                  </div>
                </div>

                <!-- Submit Actions -->
                <div class="form-actions">
                  <button type="button"
                          class="btn btn-outline-secondary"
                          [disabled]="isSubmitting"
                          (click)="router.navigateByUrl('/admin/user')">
                    Cancel
                  </button>
                  <button type="submit"
                          class="btn btn-primary"
                          [disabled]="createForm.invalid || isSubmitting">
                    @if (isSubmitting) {
                      <i class="fa-solid fa-spinner fa-spin me-1"></i>
                      @if (user_id !== null) {
                        Updating User...
                      } @else {
                        Creating User...
                      }
                    } @else {
                      @if (user_id !== null) {
                        <i class="fa-solid fa-floppy-disk me-1"></i>
                        Update User
                      } @else {
                        <i class="fa-solid fa-user-plus me-1"></i>
                        Create User
                      }
                    }
                  </button>
                </div>
              </form>
            }
          </div>
        </div>
      </div>

      <!-- Profile Image Section -->
      <div class="col-lg-4">
        <div class="form-card">
          <div class="card-section-header">
            <div class="d-flex align-items-center gap-3">
              <div class="section-icon">
                <i class="fa-solid fa-camera"></i>
              </div>
              <div>
                <h3 class="section-title">Profile Image</h3>
                <p class="section-subtitle">Upload a profile picture (optional)</p>
              </div>
            </div>
          </div>

          <div class="card-section-body">
            <!-- Upload Progress -->
            @if (uploadProgress() > 0 && uploadProgress() < 100) {
              <div class="upload-progress-area">
                <ngb-progressbar [value]="uploadProgress()"
                                type="primary"
                                class="progress-bar-custom">
                </ngb-progressbar>
                <span class="progress-label">Uploading... {{ uploadProgress() }}%</span>
              </div>
            }

            <!-- Drop Zone -->
            <div class="drop-zone"
                 [class.drop-zone-success]="uploadSuccess"
                 [class.drop-zone-error]="uploadError"
                 [class.drop-zone-uploading]="isUploadingImage"
                 (drop)="onFileDrop($event)"
                 (dragover)="onDragOver($event)"
                 (click)="fileInput?.click()">

              @if (imagePreview()) {
                <div class="preview-container">
                  <img [src]="imagePreview()"
                       alt="Profile Preview"
                       class="preview-img">
                  @if (isUploadingImage) {
                    <div class="preview-overlay">
                      <i class="fa-solid fa-spinner fa-spin fa-lg"></i>
                    </div>
                  }
                </div>
              } @else {
                <div class="drop-zone-content">
                  @if (isUploadingImage) {
                    <i class="fa-solid fa-spinner fa-spin fa-2x mb-3" style="color: var(--accent);"></i>
                    <p class="drop-zone-text">Uploading image...</p>
                  } @else {
                    <div class="drop-zone-icon">
                      <i class="fa-solid fa-cloud-arrow-up"></i>
                    </div>
                    <p class="drop-zone-title">Upload Profile Image</p>
                    <p class="drop-zone-text">Drag & drop an image here, or click to select</p>
                    <span class="drop-zone-hint">Supports: JPG, JPEG, PNG</span>
                  }
                </div>
              }

              <input type="file"
                     accept=".jpg, .jpeg, .png"
                     (change)="onFileChange($event)"
                     hidden
                     #fileInput
                     [disabled]="isUploadingImage || isSubmitting">
            </div>

            <!-- File Info -->
            @if (imageName()) {
              <div class="file-info">
                <div class="file-meta">
                  <div class="file-name">
                    <i class="fa-solid fa-file-image me-2" style="color: var(--accent);"></i>
                    {{ imageName() }}
                  </div>
                  <div class="file-size">{{ fileSize() }} KB</div>
                </div>

                <div class="file-actions">
                  <button type="button"
                          class="btn btn-outline-secondary btn-sm"
                          (click)="fileInput?.click()"
                          [disabled]="isUploadingImage || isSubmitting">
                    <i class="fa-solid fa-pen me-1"></i>
                    Change
                  </button>
                  @if (selectedFile || imagePreview()) {
                    <button type="button"
                            class="btn btn-danger btn-sm"
                            (click)="removeImage()"
                            [disabled]="isUploadingImage || isSubmitting">
                      <i class="fa-solid fa-trash me-1"></i>
                      Remove
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
